// ========================================================
//                TradingAccounts.js  (FULL)
// ========================================================
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";

import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import * as styles from "./Styles";
import { getTradingAccountsByEmail } from "../../Endpoints/TradingAccounts";
import { getTradingAccountsByTreeId } from "../../Endpoints/BrokerTrees";
import {
  buildTableRowsPerPageOptions,
  TABLE_INITIAL_ROWS_PER_PAGE,
} from "../Table/constants.js";

const ROWS_OPTIONS = buildTableRowsPerPageOptions([5, 10, 25, 50, 100]);

export const MOCK_TRADING_ACCOUNTS = Array.from({ length: 12 }).map((_, i) => ({
  id: 1012 + i,
  name: i === 0 ? "Juan Carlos Gonzalez" : "Carolina Romano",
  email: i === 0 ? "juangonzalez@gmail.com" : "cromano@tradeviewmarkets.com",
  phone: "3112215698",
  country: i === 0 ? "COL" : "ARG",
  address: i === 0 ? "Street 123, Bogotá" : "Av. 456, Buenos Aires",
  city: i === 0 ? "Bogotá" : "Buenos Aires",
  comment: i === 0 ? "VIP client" : "Standard client",
  status: i !== 3, // ejemplo

  // step 2 (account details)
  baseCurrency: i % 2 === 0 ? "USD" : "EUR",
  leverage: i % 2 === 0 ? "1:10" : "1:100",
  password: "********",
  investorPassword: "********",

  // step 3 (allocation)
  linkToTree: i % 2 === 0,
  ib: i % 2 === 0 ? "IB-001" : "IB-002",
  tree: i % 2 === 0 ? "Tree forex" : "",
  server: "Demo/Agent",
  group: i % 2 === 0 ? "Demo seas 123" : "",
  serverLabel: "Demo/Agent",
  groupLabel: "Demo seas 123",

  // tabla
  serverTable: "Demo/Agent",
  groupTable: "Demo seas 123",
  treeTable: "Tree forex",
  ccy: i % 2 === 0 ? "USD" : "EUR",
  balance: "100.000",
  equity: "100.00",
  regDate: "02/12/2019",
  regDays: "1:1",
}));

function mapTradingAccountsToRows(list = [], selectedEmail = "") {
  return list.map((item, index) => ({
    id: item?.login ?? item?.id ?? index + 1,
    login: item?.login ?? item?.id ?? "",
    name: item?.name ?? "",
    email: item?.email ?? selectedEmail,
    phone: item?.phone ?? "",
    country: item?.country ?? "",
    server: item?.server ?? "",
    group: item?.group ?? "",
    tree_name: item?.tree_name ?? item?.tree ?? item?.treeName ?? "",
    leverage: item?.leverage ?? "",
    currency: item?.currency ?? item?.ccy ?? "",
    balance: item?.balance ?? item?.accountBalance ?? "",
    equity: item?.equity ?? "",
    registration_date:
      item?.registration_date ?? item?.registrationDate ?? item?.regDate ?? item?.createdAt ?? "",
    registred_ago: item?.registred_ago ?? item?.registrationDays ?? item?.regDays ?? "",
  }));
}

function markListReturnWithoutReload(locationState) {
  try {
    if (!locationState || locationState.fromTrees || locationState.id_tree || locationState.treeId) {
      sessionStorage.setItem("brokerTrees.skipNextFetch", "1");
    }
    if (locationState?.fromBrokersIbs) {
      sessionStorage.setItem("brokersIbs.skipNextFetch", "1");
    }
  } catch (error) {
    // sessionStorage can be unavailable; the in-memory cache still covers SPA backs.
  }
}

export default function TradingAccounts() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedEmail = String(
    location.state?.email ?? location.state?.row?.email ?? ""
  ).trim();
  const selectedTreeId = String(
    location.state?.id_tree ?? location.state?.treeId ?? ""
  ).trim();
  const selectedName = String(
    location.state?.name ??
      location.state?.tree?.name ??
      location.state?.row?.name ??
      ""
  ).trim();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_INITIAL_ROWS_PER_PAGE);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    const onPopState = () => {
      markListReturnWithoutReload(location.state);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [location.state]);

  const handleGoBack = () => {
    markListReturnWithoutReload(location.state);
    navigate(-1);
  };

  useEffect(() => {
    let ignore = false;

    const fetchTradingAccounts = async () => {
      if (!selectedTreeId && !selectedEmail) {
        setRows([]);
        setFetchError("No tree or email was provided.");
        return;
      }

      try {
        setLoading(true);
        setFetchError("");

        const response = selectedTreeId
          ? await getTradingAccountsByTreeId(selectedTreeId)
          : await getTradingAccountsByEmail(selectedEmail);
        const payload = response?.data;
        const accounts = Array.isArray(payload?.data?.accounts)
          ? payload.data.accounts
          : Array.isArray(payload?.accounts)
            ? payload.accounts
            : [];

        if (!ignore) {
          setRows(mapTradingAccountsToRows(accounts, selectedEmail));
        }
      } catch (error) {
        if (!ignore) {
          setRows([]);
          setFetchError(
            error?.message ||
              "We were unable to fetch the trading accounts."
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchTradingAccounts();

    return () => {
      ignore = true;
    };
  }, [selectedEmail, selectedTreeId]);

  const rowsFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      return (
        String(r.id).toLowerCase().includes(q) ||
        String(r.login || "").toLowerCase().includes(q) ||
        String(r.name || "").toLowerCase().includes(q) ||
        String(r.email || "").toLowerCase().includes(q) ||
        String(r.phone || "").toLowerCase().includes(q) ||
        String(r.server || "").toLowerCase().includes(q) ||
        String(r.group || "").toLowerCase().includes(q) ||
        String(r.tree_name || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const pageCount = Math.max(1, Math.ceil(rowsFiltered.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  const rowsPaged = useMemo(() => {
    const start = safePage * rowsPerPage;
    return rowsFiltered.slice(start, start + rowsPerPage);
  }, [rowsFiltered, safePage, rowsPerPage]);

  const pageItems = useMemo(() => {
    const last = pageCount;
    const current = safePage + 1;

    if (last <= 5) return Array.from({ length: last }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, "…", last];
    if (current >= last - 2) return [1, "…", last - 3, last - 2, last - 1, last];
    return [1, "…", current - 1, current, current + 1, "…", last];
  }, [safePage, pageCount]);

  const arrowBtnSx = (disabled) => ({
    minWidth: 30,
    height: 30,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    color: disabled ? "#9CA3AF" : "#374151",
    boxShadow: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#D1D5DB",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#FFFFFF",
      borderColor: "#E5E7EB",
      color: "#9CA3AF",
      opacity: 1,
    },
  });

  const columns = [
    { field: "login", headerName: "Login", width: 120 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 180 },
    { field: "phone", headerName: "Phone", width: 120 },
    { field: "country", headerName: "Country", width: 90 },
    { field: "server", headerName: "Server", width: 130 },
    { field: "group", headerName: "Group", width: 140 },
    { field: "tree_name", headerName: "Tree", width: 140 },
    { field: "leverage", headerName: "Leverage", width: 100 },
    { field: "currency", headerName: "CCY", width: 80 },
    { field: "balance", headerName: "Balance", width: 110 },
    { field: "equity", headerName: "Equity", width: 100 },
    { field: "registration_date", headerName: "Reg date", width: 180 },
    { field: "registred_ago", headerName: "Reg (days)", width: 110 },
  ];

  // const handleCreateTradingAccount = () => {
  //   navigate(`/admin/trading-accounts-groups/create`, { state: { brokerOrIbId: id } });
  // };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 4,
        pt: 2,
        pb: 4,
        background: "transparent",
        backgroundColor: "transparent",
      }}
    >
      {/* Back */}
      <Button
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
        onClick={handleGoBack}
        sx={{ textTransform: "none", mb: 1.5, color: "#111827" }}
      >
        Go back
      </Button>

      {/* Title */}
      <Typography
        sx={{
          fontFamily: "Plus Jakarta Sans",
          fontSize: "26px",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "140%",
        }}
      >
        Trading accounts linked to {selectedName || "this broker or IB"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Monitor and set-up trading accounts linked to this broker or IB.
      </Typography>

      {/* Main card */}
      <Paper
        elevation={2}
        sx={{
          ...styles.MainCard__body,
          padding: "20px",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "visible",
          flexGrow: 0,
          flexShrink: 0,
          alignSelf: "flex-start",
        }}
      >
        {/* Toolbar row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            placeholder="Search by account number, name or email"
            sx={{
              width: 420,
              background: "white",
              borderRadius: "10px",
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ opacity: 0.7 }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          {/* <Button
            variant="outlined"
            sx={{ ...styles.ExportButton, ...styles.ExportButtonRed }}
            onClick={handleCreateTradingAccount}
          >
            + Create a trading account
          </Button> */}
        </Box>

        {/* Table container */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid rgba(229,231,235,0.9)",
            background: "rgba(255,255,255,0.9)",
          }}
        >
          {fetchError ? (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography sx={{ color: "#B42318", fontSize: 14 }}>
                {fetchError}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ height: 430, width: "100%" }}>
            {loading ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={28} />
              </Box>
            ) : (
              <DataGrid
                rows={rowsPaged}
                columns={columns}
                hideFooter
                rowHeight={58}
                headerHeight={56}
                disableRowSelectionOnClick
                sx={{
                  border: 0,
                  "& .MuiDataGrid-columnHeaders": {
                    background: "#F9FAFB",
                    color: "#374151",
                    fontWeight: 800,
                  },
                  "& .MuiDataGrid-cell": { color: "#374151" },
                  "& .MuiDataGrid-row:hover": { background: "#F9FAFB" },
                  "& .MuiDataGrid-virtualScroller": { background: "transparent" },
                }}
              />
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              flexWrap: "wrap",
              borderTop: "1px solid rgba(229,231,235,0.9)",
              background: "white",
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
              Total:&nbsp; <b style={{ color: "#111827" }}>{rowsFiltered.length}</b>
            </Typography>

            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
              Pages:&nbsp; <b style={{ color: "#111827" }}>{pageCount}</b>
            </Typography>

            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>

            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              sx={{ height: 32, minWidth: 70, borderRadius: 2, ".MuiSelect-select": { py: 0.5 } }}
              MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
            >
              {ROWS_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                sx={arrowBtnSx(safePage === 0)}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </Button>

              {pageItems.map((it, idx) => {
                if (it === "…") {
                  return (
                    <Typography key={`dots-${idx}`} sx={{ fontSize: 14, color: "#9CA3AF", px: 0.5 }}>
                      …
                    </Typography>
                  );
                }

                const pageNumber = it;
                const isActive = pageNumber === safePage + 1;

                return (
                  <Button
                    key={`p-${pageNumber}`}
                    onClick={() => setPage(pageNumber - 1)}
                    size="small"
                    sx={{
                      minWidth: 28,
                      height: 28,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: isActive ? 900 : 700,
                      color: isActive ? "#111827" : "#6B7280",
                      backgroundColor: isActive ? "#EEF2FF" : "transparent",
                      "&:hover": { backgroundColor: isActive ? "#EEF2FF" : "#F3F4F6" },
                    }}
                  >
                    {pageNumber}
                  </Button>
                );
              })}

              <Button
                size="small"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={safePage >= pageCount - 1}
                sx={arrowBtnSx(safePage >= pageCount - 1)}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          </Box>
        </Paper>
      </Paper>
    </Box>
  );
}